import { useMemo } from 'react';

import {
  ChartTimeSeriesLine,
  ChartTimeSeriesLineProps,
  ChartTimeSeriesLineTheme,
} from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { CERBERUS_STATS_FIELDS, CerberusStatsFields } from '@hcs/types';
import { calcMobileMarginAdjustment, logWarning } from '@hcs/utils';

import { CERBERUS_STATS_FIELD_CONFIGS } from '../../configs';
import { PropertyTypeWarning } from '../../features';
import {
  CerberusStatsListingStats,
  CerberusStatsRentalListingStats,
} from '../../types';
import { getCerberusStatsFieldValue } from '../../utils';

export interface CerberusStatsListingStatsChartProps {
  dataHcName: string;
  className?: string;
  theme?: Partial<ChartTimeSeriesLineTheme>;
  cerberusStatsField: CerberusStatsFields;
  isRentalField: boolean;
  isLoading: boolean;
  showPropertyTypeWarning?: boolean;
  data:
    | Array<CerberusStatsListingStats | CerberusStatsRentalListingStats>
    | null
    | undefined;
  propertyType: 'singleFamily' | 'condo';
  setPropertyType?: (propertyType: 'singleFamily' | 'condo') => void;
}

export const CerberusStatsListingStatsChart = ({
  className,
  dataHcName,
  cerberusStatsField,
  isRentalField,
  isLoading,
  showPropertyTypeWarning,
  data,
  theme,
  propertyType,
  setPropertyType,
}: CerberusStatsListingStatsChartProps) => {
  const mergedTheme = {
    Container: className,
    ...theme,
  };
  const { isPhoneWidth } = useScreenSize();
  const fieldConfig = CERBERUS_STATS_FIELD_CONFIGS[cerberusStatsField];
  const chartData: ChartTimeSeriesLineProps['chartData'] = useMemo(() => {
    if (!data) {
      return null;
    }
    return [
      {
        id: cerberusStatsField,
        data:
          data?.map((record) => {
            return {
              x: `${getCerberusStatsFieldValue(
                isRentalField
                  ? CERBERUS_STATS_FIELDS.listingStatsEventDateRental
                  : CERBERUS_STATS_FIELDS.listingStatsEventDate,
                record,
              )}`,
              y: getCerberusStatsFieldValue(cerberusStatsField, record),
            };
          }) || [],
      },
    ];
  }, [data, isRentalField, cerberusStatsField]);
  if (!fieldConfig) {
    logWarning(
      `CerberusStatsListingStatsChart: no config found for field ${cerberusStatsField}`,
    );
    return null;
  }

  const mobileMargin =
    isPhoneWidth && chartData?.[0].data
      ? calcMobileMarginAdjustment(chartData?.[0].data, fieldConfig.d3FormatStr)
      : undefined;

  const currentListingStats = data ? data[data.length - 1] : null;
  const Display = fieldConfig.Display;
  // Check if last element in ListingStats (current stat) exists.
  // If not, set ChartTimeSeriesLine chartData to null, triggering No Data to render.
  const currentListingStat = fieldConfig.getValue(currentListingStats);

  return (
    <>
      <ChartTimeSeriesLine
        dataHcName={dataHcName}
        isLoading={isLoading}
        chartData={currentListingStat != null ? chartData : null}
        title={fieldConfig.label}
        CurrentValue={<Display cerberusStats={currentListingStats} />}
        d3FormatterForY={fieldConfig.d3FormatStr}
        rightAxisMargin={mobileMargin}
        tickRotationOverride={isPhoneWidth ? -60 : undefined}
        theme={mergedTheme}
        compactTitle={isPhoneWidth}
        compactValue={isPhoneWidth}
      />
      {showPropertyTypeWarning && (
        <PropertyTypeWarning
          dataHcName="market-insights-property-type-toggle"
          propertyType={propertyType}
          setPropertyType={setPropertyType}
        />
      )}
    </>
  );
};
